import { Matrix3, Matrix4, Vector3 } from "three";
import SceneObjectData from "./SceneObjectData";
import SceneObjectTypeEnum from "./SceneObjectTypeEnum";
import { vector3FromJson, vector3ToJson } from "../../services/bimVisualizerApi/primitivesSerialization";
import Constants from "../../constants";
import { mmFromSceneUnits, sceneUnitsFromMm } from "../../utils/scaler";
import { UUID } from "../../utils/typeDefinitions";
import checklistItemDataSchema from "../../services/bimVisualizerApi/schema/document/sceneObject/checklistItemDataSchema";

class ChecklistItemData extends SceneObjectData {
    position: Vector3;
    normal: Vector3;
    checklistItemId: string | null;

    constructor(
        position: Vector3,
        checklistItemId: string | null = null,
        uuid?: UUID,
        parentId?: UUID,
        normal: Vector3 = new Vector3(0, 1, 0)
    ) {
        super(SceneObjectTypeEnum.ChecklistItem, uuid, parentId);
        this.position = position;
        this.normal = normal.clone().normalize();
        this.checklistItemId = checklistItemId;
    }

    clone(): this {
        const clone = new ChecklistItemData(
            this.position.clone(),
            this.checklistItemId,
            this.uuid,
            this.parentId,
            this.normal.clone()
        ) as this;

        this.copyBaseData(clone);
        return clone;
    }

    applyMatrix4(matrix: Matrix4): void {
        this.position.applyMatrix4(matrix);
        const normalMatrix = new Matrix3().getNormalMatrix(matrix);
        this.normal.applyMatrix3(normalMatrix).normalize();
    }

    toJson(): any {
        return {
            ...super.toJson(),
            position: vector3ToJson(this.position, Constants.IO_LOCATION_PRECISION, mmFromSceneUnits),
            normal: vector3ToJson(this.normal, Constants.IO_DIRECTION_PRECISION),
            checklistItemId: this.checklistItemId ?? null,
        };
    }

    static fromJson(json: any): ChecklistItemData {
        const parsedData = checklistItemDataSchema.parse(json);

        const normal = parsedData.normal
            ? vector3FromJson(parsedData.normal)
            : new Vector3(0, 1, 0);

        return new ChecklistItemData(
            vector3FromJson(parsedData.position, sceneUnitsFromMm),
            parsedData.checklistItemId ?? null,
            parsedData.uuid,
            parsedData.parentId ?? undefined,
            normal
        );
    }
}

export default ChecklistItemData;
